import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, NestedDialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { useUser } from "@/hooks/auth";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ExperienceSection } from "@/components/profile/ExperienceSection";
import { EducationSection } from "@/components/profile/EducationSection";
import { ProjectsSection } from "@/components/profile/ProjectsSection";
import { CertificationsSection } from "@/components/profile/CertificationsSection";
import { AwardsSection } from "@/components/profile/AwardsSection";
import { InlineLoader } from '@/components/ui/loading';
import { useTranslation } from '@/hooks/useTranslation';

interface Profile {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  avatar_url: string;
  headline: string;
  current_position: string;
  current_company: string;
  location: string;
  summary: string;
  website: string;
  linkedin_url: string;
  github_url: string;
  user_type: string;
  created_at: string;
}

interface UserProfileDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  userId?: string;
  candidateEmail?: string;
  /** Use nested=true when opening from another dialog to ensure proper z-index layering */
  nested?: boolean;
}

export function UserProfileDialog({ open, onOpenChange, onClose, userId, candidateEmail, nested = false }: UserProfileDialogProps) {
  const { t } = useTranslation('ui');
  const handleOpenChange = (open: boolean) => {
    if (onOpenChange) {
      onOpenChange(open);
    } else if (onClose && !open) {
      onClose();
    }
  };
  
  const toast = useTranslatedToast();
  const { user } = useUser();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Only allow editing if viewing own profile
  const canEdit = user?.id === userId;

  useEffect(() => {
    const controller = new AbortController();
    
    if (open && userId) {
      fetchUserData();
    }
    
    return () => {
      controller.abort(); // Cancel pending requests on unmount/close
    };
  }, [open, userId]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setNotFound(false);

      const targetUserId = userId;

      if (!targetUserId) {
        console.log('No user ID available');
        setNotFound(true);
        return;
      }

      // Fetch profile data with additional fields for editing
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', targetUserId as any)
        .maybeSingle();

      if (profileError) {
        console.error('Profile fetch error:', profileError);
        if (profileError.code === 'PGRST116') {
          console.log('No profile found for user:', targetUserId);
          setNotFound(true);
          return;
        }
        throw profileError;
      }

      if (!profileData) {
        console.log('No profile data returned for user:', targetUserId);
        setNotFound(true);
        return;
      }

      setProfile(profileData as any);
    } catch (error) {
      console.error('Error fetching user data:', error);
      error("Failed to load profile data", {
        title: "Error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    await fetchUserData();
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  // Use NestedDialogContent when opened from another dialog, DialogContent otherwise
  const ContentComponent = nested ? NestedDialogContent : DialogContent;

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <ContentComponent className="w-[calc(100vw-4rem)] h-[calc(100vh-4rem)] max-w-none max-h-none overflow-y-auto" style={{ width: "calc(100vw - 4rem)", height: "calc(100vh - 4rem)" }}>
          <DialogTitle className="sr-only">User Profile</DialogTitle>
          <DialogDescription className="sr-only">Loading user profile information</DialogDescription>
          <div className="text-center space-y-4 py-8">
            <InlineLoader size="lg" text={t('loading.profile')} />
          </div>
        </ContentComponent>
      </Dialog>
    );
  }

  if (notFound || !profile) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <ContentComponent className="w-[calc(100vw-4rem)] h-[calc(100vh-4rem)] max-w-none max-h-none overflow-y-auto" style={{ width: "calc(100vw - 4rem)", height: "calc(100vh - 4rem)" }}>
          <DialogTitle className="sr-only">Profile Not Found</DialogTitle>
          <DialogDescription className="sr-only">The requested user profile could not be found</DialogDescription>
          <div className="text-center space-y-4 py-8">
            <User className="h-16 w-16 mx-auto text-muted-foreground" />
            <h1 className="text-2xl font-bold">Profile not found</h1>
            <p className="text-muted-foreground">The user profile you're looking for doesn't exist.</p>
          </div>
        </ContentComponent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <ContentComponent className="w-[calc(100vw-4rem)] h-[calc(100vh-4rem)] max-w-none max-h-none overflow-y-auto" style={{ width: "calc(100vw - 4rem)", height: "calc(100vh - 4rem)" }}>
          <DialogTitle className="sr-only">User Profile Details</DialogTitle>
          <DialogDescription className="sr-only">Detailed view of user profile, experience, and information</DialogDescription>
          

          <div className="space-y-6 p-4">
            {/* Profile Header */}
            <ProfileHeader
              profile={profile}
              user={user}
              isEditing={isEditing}
              onEditToggle={handleEditToggle}
              onProfileUpdate={handleProfileUpdate}
              nested={true}
            />

            {/* Experience Section */}
            <ExperienceSection 
              userId={userId} 
              isEditing={isEditing && canEdit} 
            />

            {/* Education Section */}
            <EducationSection 
              userId={userId} 
              isEditing={isEditing && canEdit} 
            />

            {/* Projects Section */}
            <ProjectsSection 
              userId={userId} 
              isEditing={isEditing && canEdit} 
            />

            {/* Certifications Section */}
            <CertificationsSection 
              userId={userId} 
              isEditing={isEditing && canEdit} 
            />

            {/* Awards Section */}
            <AwardsSection 
              userId={userId} 
              isEditing={isEditing && canEdit} 
            />
          </div>
        </ContentComponent>
      </Dialog>

    </>
  );
}